import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Database,
  Inbox,
  LogOut,
  Layers,
  List,
  Menu,
  Mail,
  Archive,
  Award,
  Route,
  Shield,
  SlidersHorizontal,
  Terminal,
  User,
  Users,
  Wrench,
  X,
} from 'lucide-react';

const childIsActive = (pathname, child, siblings) => {
  if (pathname === child.href) return true;
  if (!child.matchNested || !pathname.startsWith(`${child.href}/`)) return false;
  return !siblings.some(
    (other) =>
      other.href !== child.href &&
      other.href.length > child.href.length &&
      (pathname === other.href || pathname.startsWith(`${other.href}/`))
  );
};

const groupContainsPath = (item, pathname) =>
  Boolean(
    item.children?.some(
      (child) =>
        pathname === child.href ||
        (child.matchNested && pathname.startsWith(`${child.href}/`))
    )
  );

const Layout = ({ children }) => {
  const { logout, user, isAdmin, hasFeature } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Nav follows the same rules the API enforces: a feature gates the pages that
  // belong to it, and admins hold every feature implicitly (/auth/me resolves
  // them to the full catalog, so hasFeature is true throughout).
  //
  // The Service Inbox page is served from here but its data is not:
  // /api/inbox/review/* belongs to the service inbox, which owns both the emails
  // and the marks.
  // Bronze, Silver, Commands and User Management stay admin-only whatever
  // features are held: they trigger syncs, refresh gold and delete data.
  // One entry per feature. There is no Dashboard entry: "/" no longer renders a
  // page at all, it redirects to whatever the account can actually use, which
  // for an admin is Time Analysis.
  //
  // Inside DataSync, Bronze and Silver are readable with the feature while
  // Commands stays admin-only, matching the API: every GET in those routers is
  // gated on datasync, every sync trigger and config toggle on admin.
  const getNavigationItems = () => [
    {
      name: 'DataSync',
      icon: Database,
      show: isAdmin() || hasFeature('datasync'),
      children: [
        { name: 'Bronze Layer', href: '/bronze', icon: Archive, show: true },
        { name: 'Silver Layer', href: '/silver', icon: Layers, show: true },
        { name: 'Commands', href: '/commands', icon: Terminal, show: isAdmin() },
      ].filter((child) => child.show),
    },
    { name: 'Time Analysis', href: '/gold', icon: Award, show: hasFeature('time_analysis') },
    {
      // Mailbox and Review come with the service_inbox feature; Task Routing stays
      // admin-only, matching the API (routing writes are gated on the admin
      // claim alone). Admins resolve to the full catalog, so hasFeature is true
      // for them throughout.
      name: 'Service Inbox',
      icon: ClipboardCheck,
      show: hasFeature('service_inbox') || isAdmin(),
      children: [
        { name: 'Mailbox', href: '/mailbox', icon: Inbox, show: hasFeature('service_inbox') },
        { name: 'Ops Dashboard', href: '/digest', icon: BarChart3, show: hasFeature('service_inbox') },
        { name: 'Task Routing', href: '/routing', icon: Route, show: isAdmin() },
        { name: 'Classification Review', href: '/review', icon: ClipboardCheck, show: hasFeature('service_inbox') },
      ].filter((child) => child.show),
    },
    {
      name: 'Audit Tool',
      icon: Shield,
      show: hasFeature('audit'),
      children: [
        { name: 'Summary', href: '/audit/summary', icon: BarChart3, show: true, matchNested: true },
        { name: 'Audits', href: '/audit', icon: List, show: true, matchNested: true },
        { name: 'Technicians', href: '/audit/technicians', icon: Wrench, show: true, matchNested: true },
        { name: 'Notifications', href: '/audit/notifications', icon: Mail, show: isAdmin() },
        { name: 'Scoring Admin', href: '/audit/scoring', icon: SlidersHorizontal, show: true },
      ].filter((child) => child.show),
    },
    { name: 'User Management', href: '/users', icon: Users, show: isAdmin() },
  ].filter((item) => item.show);

  const navigation = getNavigationItems();

  // A group opens itself when you are on one of its pages -- including on a hard
  // refresh, which is why this is an effect on pathname and not just initial
  // state. Toggling it by hand afterwards sticks, until you navigate into a
  // group that is closed.
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    const containingGroup = navigation.find((item) => groupContainsPath(item, location.pathname));
    if (containingGroup) {
      setOpenGroups((prev) => ({ ...prev, [containingGroup.name]: true }));
    }
    // Keyed on pathname alone: navigation is rebuilt every render, so depending
    // on it would re-run this effect forever.
  }, [location.pathname]); // eslint-disable-line

  const toggleGroup = (name) => {
    setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Below md the sidebar is a drawer behind a menu button in a top bar. It
  // closes on every navigation, on the backdrop, and on Escape.
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // The page's own name, for the phone top bar.
  const pageName = (() => {
    for (const item of navigation) {
      if (item.href && location.pathname === item.href) return item.name;
      const child = item.children?.find((c) => childIsActive(location.pathname, c, item.children));
      if (child) return child.name;
    }
    return '';
  })();

  const sidebar = (
    <div className="flex h-full flex-col w-64 bg-gray-800">
      <div className="flex items-center justify-center h-16 px-4 bg-gray-900">
        <Database className="h-8 w-8 text-blue-400 mr-2" />
        <h1 className="text-white text-lg font-semibold">DelAir</h1>
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;

            if (item.children) {
              const expanded = openGroups[item.name];
              return (
                <div key={item.name}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.name)}
                    aria-expanded={expanded}
                    className="w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    <span className="flex-1 text-left">{item.name}</span>
                    {expanded
                      ? <ChevronDown className="h-4 w-4" />
                      : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {expanded && (
                    <div className="mt-1 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isActive = childIsActive(location.pathname, child, item.children);
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`group flex items-center pl-9 pr-2 py-2 text-sm font-medium rounded-md ${
                              isActive
                                ? 'bg-gray-900 text-white'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            <ChildIcon className="mr-3 h-4 w-4" />
                            {child.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = location.pathname === item.href;

            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-gray-700 p-4">
          <div className="flex items-center mb-3">
            <div className="flex items-center justify-center w-8 h-8 bg-gray-600 rounded-full">
              <User className="h-4 w-4 text-gray-300" />
            </div>
            <div className="flex-1 ml-3">
              <p className="text-sm font-medium text-white">{user?.username || 'User'}</p>
              <p className="text-xs text-gray-400">
                {isAdmin() ? 'Administrator' : 'Tenant User'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );

  return (
    // 100dvh where the browser has it (inline, so an older one falls back to
    // h-screen): on a phone 100vh includes the space under the address bar,
    // which cut off the bottom of every page.
    <div className="flex h-screen bg-gray-100" style={{ height: '100dvh' }}>
      {/* Sidebar: fixed from md up */}
      <div className="hidden md:flex">{sidebar}</div>

      {/* Sidebar: a drawer below md */}
      {/* Invisible once closed (after the slide), so its links take no focus. */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-[visibility] duration-200 ${
          navOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity ${navOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setNavOpen(false)}
        />
        <div
          className={`absolute inset-y-0 left-0 transition-transform duration-200 ${
            navOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {sidebar}
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            className="absolute right-2 top-4 rounded p-1 text-gray-300 hover:bg-gray-700 hover:text-white"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar below md: the menu button and where you are */}
        <div className="md:hidden flex items-center h-12 shrink-0 gap-2 px-2 bg-gray-800 text-white">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="rounded p-1.5 hover:bg-gray-700"
            aria-label="Open menu"
            aria-expanded={navOpen}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Database className="h-5 w-5 text-blue-400" />
          <span className="font-semibold">DelAir</span>
          {pageName && <span className="truncate text-sm text-gray-300">· {pageName}</span>}
        </div>
        <main className="flex-1 overflow-auto p-3 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 